import CardState, { type CardStateValue } from '@/components/ui/CardState'
import SectionHead from '@/components/ui/SectionHead'
import SeeAllLink from '@/components/ui/SeeAllLink'
import { stockLinkOptions } from '@/components/ui/symbol-link'
import RailNewsRow, { RailNewsRowSkeleton } from '@/features/market-rail/components/RailNewsRow'
import RailRows from '@/features/market-rail/components/RailRows'
import { useRailBlock } from '@/features/market-rail/useRailFit'
import { useIncome } from '@/features/portfolio/api/queries'
import { formatBsDate } from '@/lib/bsDate'
import { useActiveShareholderId } from '@/screens/portfolio/components/portfolio-switcher/activeProfile'
import { upcomingIncomeKey, type UpcomingIncome } from '@/schemas/portfolio'

/** The mock draws three; the rail's fit trims further on a short window. */
const LIMIT = 3

const SKELETON_ROWS = Array.from({ length: LIMIT }, (_, index) => (
  <RailNewsRowSkeleton key={index} />
))

const KIND: Record<UpcomingIncome['kind'], string> = {
  CASH: 'Dividends',
  BONUS: 'Bonus shares',
  RIGHTS: 'Rights',
}

/** "Dividends · 2 Bhadra 2083", as the mock's meta line; an undated one is only announced. */
const metaOf = (row: UpcomingIncome): string =>
  `${KIND[row.kind]} · ${row.bookClosureDate === null ? 'Announced' : `book closes ${formatBsDate(row.bookClosureDate)}`}`

/**
 * What's happening (mock `AW`): the corporate actions announced on the companies the member holds
 * — cash dividends, bonus shares and rights — off `GET /portfolio/income/`'s `upcoming`, the one
 * feed of held-stock events the backend serves (the mock's AGM rows need a per-symbol events read,
 * ask P2). Each row opens the company; "More" goes to Income, whose Coming up card lists them all.
 *
 * `detail` is the backend's own pre-formatted English ("Rs 12.00 per share") and is printed as is.
 */
export default function HappeningBlock() {
  const block = useRailBlock('happening')
  const shareholderId = useActiveShareholderId()
  const income = useIncome(shareholderId)
  const rows = income.data?.upcoming

  const state: CardStateValue = income.isPending
    ? 'loading'
    : income.isError
      ? 'failed'
      : (rows?.length ?? 0) === 0
        ? 'empty'
        : 'loaded'

  return (
    <section aria-label="What's happening" {...block}>
      <SectionHead
        size="sm"
        title="What's happening"
        action={
          <SeeAllLink to="/portfolio/income" size="rail" aria-label="More on what's coming up">
            More
          </SeeAllLink>
        }
      />
      <div className="mt-2">
        <CardState
          state={state}
          skeleton={<RailRows block="happening" rows={SKELETON_ROWS} />}
          onRetry={() => {
            void income.refetch()
          }}
          failedTitle="Couldn't load what's coming up"
          failedDescription="Nothing else on this page is affected."
          emptyTitle="Nothing announced"
          emptyDescription="Dividends, bonus shares and rights on the companies you hold show here."
        >
          {rows === undefined ? null : (
            <RailRows
              block="happening"
              rows={rows.slice(0, LIMIT).map((row) => (
                <RailNewsRow
                  key={upcomingIncomeKey(row)}
                  meta={[metaOf(row)]}
                  title={`${row.symbol} · ${row.detail}`}
                  link={stockLinkOptions(row.symbol)}
                />
              ))}
            />
          )}
        </CardState>
      </div>
    </section>
  )
}
